import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  Play, Clock, Calendar, Users, Star, Lock, Download, CheckCircle,
  BookOpen, Video, Award, Book, Share2, Heart, ArrowLeft,
  FileText, Loader2, AlertCircle, User, CreditCard, Trophy, CheckCheck,
  RotateCcw
} from 'lucide-react';
import QuizComponent from '../components/QuizComponent';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CONTENT_TYPE_ICONS = {
  curso: BookOpen,
  webinar: Video,
  masterclass: Award,
  diplomado: Book
};

export default function ContentDetailPage() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState(null);
  const [modules, setModules] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [userStatus, setUserStatus] = useState(null);
  const [registering, setRegistering] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [markingComplete, setMarkingComplete] = useState(false);
  const [generatingCert, setGeneratingCert] = useState(false);
  const [error, setError] = useState('');
  const [videoProgress, setVideoProgress] = useState(0);
  const [videoDuration, setVideoDuration] = useState(0);
  const playerRef = useRef(null);
  const progressIntervalRef = useRef(null);
  const token = localStorage.getItem('upim_token');
  const user = token ? JSON.parse(localStorage.getItem('upim_user') || '{}') : null;

  // Derived state - defined early for hooks
  const canWatch = userStatus?.can_access;
  const isRegistered = userStatus?.is_registered;

  useEffect(() => {
    loadContent();
    
    // Check for payment success
    const paymentStatus = searchParams.get('payment');
    const sessionId = searchParams.get('session_id');
    if (paymentStatus === 'success' && sessionId) {
      checkPaymentStatus(sessionId);
    }
  }, [slug, searchParams]);

  // YouTube Player initialization and progress tracking
  useEffect(() => {
    if (!canWatch || !content?.youtube_video_id) return;
    
    // Load YouTube IFrame API
    if (!window.YT) {
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      const firstScriptTag = document.getElementsByTagName('script')[0];
      firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
    }
    
    window.onYouTubeIframeAPIReady = () => {
      if (playerRef.current) return;
      
      playerRef.current = new window.YT.Player('youtube-player', {
        videoId: content.youtube_video_id,
        playerVars: {
          autoplay: 0,
          modestbranding: 1,
          rel: 0
        },
        events: {
          onStateChange: onPlayerStateChange,
          onReady: onPlayerReady
        }
      });
    };
    
    // If API already loaded
    if (window.YT && window.YT.Player) {
      window.onYouTubeIframeAPIReady();
    }
    
    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    };
  }, [canWatch, content?.youtube_video_id]);

  const onPlayerReady = (event) => {
    const duration = event.target.getDuration();
    setVideoDuration(duration);
  };

  const onPlayerStateChange = async (event) => {
    if (event.data === 1) { // Playing
      progressIntervalRef.current = setInterval(() => {
        if (playerRef.current) {
          const currentTime = playerRef.current.getCurrentTime();
          const duration = playerRef.current.getDuration();
          if (duration > 0) {
            const progress = (currentTime / duration) * 100;
            setVideoProgress(progress);
            
            if (Math.floor(currentTime) % 30 === 0 && token && content?.id) {
              saveWatchProgress(currentTime, duration);
            }
          }
        }
      }, 1000);
    } else if (event.data === 2 || event.data === 0) {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
      
      if (playerRef.current && token && content?.id) {
        const currentTime = playerRef.current.getCurrentTime();
        const duration = playerRef.current.getDuration();
        saveWatchProgress(currentTime, duration);
      }
      
      if (event.data === 0 && videoProgress >= 90) {
        handleMarkComplete();
      }
    }
  };

  const saveWatchProgress = async (watchedSeconds, totalDuration) => {
    try {
      await axios.post(
        `${API_URL}/api/educational/progress/${content?.id}/watch-time`,
        {
          watched_seconds: Math.floor(watchedSeconds),
          total_duration_seconds: Math.floor(totalDuration)
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (err) {
      console.error('Error saving watch progress:', err);
    }
  };

  const checkPaymentStatus = async (sessionId) => {
    try {
      const response = await axios.get(`${API_URL}/api/educational/payment-status/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.status === 'completed') {
        setPaymentSuccess(true);
        loadContent(); // Reload to get updated access
      }
    } catch (error) {
      console.error('Error checking payment:', error);
    }
  };

  const loadContent = async () => {
    try {
      setLoading(true);
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API_URL}/api/educational/catalog/${slug}`, { headers });
      setContent(response.data.content);
      setModules(response.data.modules || []);
      setMaterials(response.data.materials || []);
      setUserStatus(response.data.user_status);
    } catch (error) {
      console.error('Error loading content:', error);
      setError('No se pudo cargar el contenido');
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async () => {
    if (!token) {
      navigate('/login');
      return;
    }

    try {
      setProcessingPayment(true);
      const formData = new FormData();
      formData.append('origin_url', window.location.origin);
      
      const response = await axios.post(
        `${API_URL}/api/educational/checkout/${content.id}`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        // Already has access
        setPaymentSuccess(true);
        loadContent();
      }
    } catch (error) {
      setError(error.response?.data?.detail || 'Error al procesar el pago');
    } finally {
      setProcessingPayment(false);
    }
  };

  const handleRegister = async () => {
    if (!token) {
      navigate('/login');
      return;
    }

    // If content is paid, redirect to payment
    if (!content.is_free && content.price > 0) {
      handlePayment();
      return;
    }

    try {
      setRegistering(true);
      await axios.post(
        `${API_URL}/api/educational/register`,
        { content_id: content.id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      loadContent(); // Reload to update status
    } catch (error) {
      setError(error.response?.data?.detail || 'Error al registrarse');
    } finally {
      setRegistering(false);
    }
  };

  const handleMarkComplete = async () => {
    if (!token || !content?.id) return;
    
    try {
      setMarkingComplete(true);
      await axios.post(
        `${API_URL}/api/educational/progress/${content.id}/complete`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      loadContent(); // Reload to update progress
    } catch (error) {
      setError(error.response?.data?.detail || 'Error al marcar como completado');
    } finally {
      setMarkingComplete(false);
    }
  };

  const handleGenerateCertificate = async () => {
    if (!token || !content?.id) return;
    
    try {
      setGeneratingCert(true);
      // Use new recognition system for constancias
      const response = await axios.post(
        `${API_URL}/api/recognition/generate-constancia/${content.id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.constancia?.download_url) {
        window.open(`${API_URL}${response.data.constancia.download_url}`, '_blank');
      }
      
      // Check if user got a new recognition
      if (response.data.new_recognition) {
        alert(`¡Felicidades! Has obtenido el reconocimiento: ${response.data.new_recognition.program_name}`);
      }
      
      loadContent();
    } catch (error) {
      setError(error.response?.data?.detail || 'Error al generar constancia');
    } finally {
      setGeneratingCert(false);
    }
  };

  const handleDownloadCertificate = async () => {
    if (!token || !content?.id) return;
    
    try {
      // Use new recognition system
      const response = await axios.post(
        `${API_URL}/api/recognition/generate-constancia/${content.id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.constancia?.download_url) {
        window.open(`${API_URL}${response.data.constancia.download_url}`, '_blank');
      }
    } catch (error) {
      setError(error.response?.data?.detail || 'Error al descargar constancia');
    }
  };

  const handleModuleClick = async (module) => {
    if (!token || !content?.id || !module?.id) return;
    
    // Check if already completed
    if (userStatus?.completed_modules?.includes(module.id)) {
      // Just play the video - module already completed
      return;
    }
    
    try {
      await axios.post(
        `${API_URL}/api/educational/progress/${content.id}/module/${module.id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      loadContent(); // Reload to update progress
    } catch (error) {
      console.error('Error marking module complete:', error);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-MX', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    }).format(price);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (error && !content) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 mx-auto text-red-400 mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Contenido no encontrado</h2>
          <p className="text-gray-500 mb-4">{error}</p>
          <Link to="/masterclasses" className="text-indigo-600 hover:text-indigo-700">
            ← Volver al catálogo
          </Link>
        </div>
      </div>
    );
  }

  const IconComponent = CONTENT_TYPE_ICONS[content?.content_type] || Video;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="content-detail-page">
      {/* Back Button */}
      <div className="max-w-7xl mx-auto px-4 py-4">
        <button
          onClick={() => navigate('/masterclasses')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-4 h-4" />
          Volver al catálogo
        </button>
      </div>

      {/* Hero Section */}
      <div className="bg-gradient-to-r from-indigo-900 via-purple-900 to-indigo-800">
        <div className="max-w-7xl mx-auto px-4 py-8 lg:py-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            {/* Info */}
            <div className="text-white">
              {/* Type Badge */}
              <div className="flex items-center gap-2 mb-4">
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-white/20 rounded-full text-sm font-medium">
                  <IconComponent className="w-4 h-4" />
                  {content?.content_type_info?.name || content?.content_type}
                </span>
                {content?.status === 'live' && (
                  <span className="px-3 py-1 bg-red-500 rounded-full text-sm font-bold animate-pulse">
                    🔴 EN VIVO
                  </span>
                )}
              </div>

              <h1 className="text-3xl lg:text-4xl font-bold mb-4">
                {content?.title}
              </h1>

              {content?.speaker_name && (
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                    <User className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="font-medium">{content.speaker_name}</p>
                    {content.speaker_title && (
                      <p className="text-sm text-indigo-200">{content.speaker_title}</p>
                    )}
                  </div>
                </div>
              )}

              {/* Meta */}
              <div className="flex flex-wrap gap-4 text-indigo-200 mb-6">
                {content?.scheduled_start && (
                  <span className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    {formatDate(content.scheduled_start)}
                  </span>
                )}
                {content?.duration_minutes && (
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {content.duration_minutes} minutos
                  </span>
                )}
                {content?.total_registrations > 0 && (
                  <span className="flex items-center gap-1">
                    <Users className="w-4 h-4" />
                    {content.total_registrations} inscritos
                  </span>
                )}
              </div>

              {/* Tags */}
              {content?.tags?.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-6">
                  {content.tags.map((tag, i) => (
                    <span key={i} className="px-2 py-1 bg-white/10 rounded text-sm">
                      {tag}
                    </span>
                  ))}
                </div>
              )}

              {/* Rating */}
              {content?.average_rating > 0 && (
                <div className="flex items-center gap-2 mb-6">
                  <div className="flex text-yellow-400">
                    {[1, 2, 3, 4, 5].map(i => (
                      <Star
                        key={i}
                        className={`w-5 h-5 ${i <= content.average_rating ? 'fill-current' : ''}`}
                      />
                    ))}
                  </div>
                  <span className="text-indigo-200">
                    {content.average_rating.toFixed(1)} ({content.total_ratings} reseñas)
                  </span>
                </div>
              )}
            </div>

            {/* Video/Thumbnail */}
            <div className="relative aspect-video rounded-xl overflow-hidden shadow-2xl">
              {canWatch && content?.youtube_video_id ? (
                <div className="w-full h-full">
                  <div id="youtube-player" className="w-full h-full" />
                  {/* Video Progress Bar */}
                  {videoProgress > 0 && (
                    <div className="absolute bottom-0 left-0 right-0 h-1 bg-black/30">
                      <div 
                        className="h-full bg-green-500 transition-all duration-300"
                        style={{ width: `${videoProgress}%` }}
                      />
                    </div>
                  )}
                </div>
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-indigo-600 to-purple-700 flex items-center justify-center">
                  {content?.thumbnail_url ? (
                    <img
                      src={content.thumbnail_url}
                      alt={content.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <IconComponent className="w-20 h-20 text-white/30" />
                  )}
                  {!canWatch && (
                    <div className="absolute inset-0 bg-black/60 flex flex-col items-center justify-center">
                      <Lock className="w-12 h-12 text-white mb-3" />
                      <p className="text-white font-medium">Contenido exclusivo para miembros</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Details */}
          <div className="lg:col-span-2 space-y-8">
            {/* Description */}
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Descripción</h2>
              <div className="prose prose-gray max-w-none">
                {content?.description?.split('\n').map((paragraph, i) => (
                  <p key={i} className="text-gray-600 mb-3">{paragraph}</p>
                ))}
              </div>
            </div>

            {/* Content Type Info */}
            {content?.content_type_info && (
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h2 className="text-xl font-bold text-gray-900 mb-4">
                  ¿Qué es un {content.content_type_info.name}?
                </h2>
                <p className="text-gray-600 mb-4">{content.content_type_info.description}</p>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-sm text-gray-500">Duración típica</p>
                    <p className="font-medium text-gray-900">{content.content_type_info.typical_duration}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-sm text-gray-500">Sesiones</p>
                    <p className="font-medium text-gray-900">{content.content_type_info.sessions}</p>
                  </div>
                </div>
                {content.content_type_info.includes && (
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-2">Incluye:</p>
                    <ul className="space-y-2">
                      {content.content_type_info.includes.map((inc, i) => (
                        <li key={i} className="flex items-center gap-2 text-gray-600">
                          <CheckCircle className="w-4 h-4 text-green-500 shrink-0" />
                          {inc}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* Quiz Section - Show if registered and content has certificate */}
            {isRegistered && content?.has_certificate && (
              <QuizComponent 
                contentId={content.id}
                token={token}
                onQuizPassed={() => loadContent()}
              />
            )}

            {/* Modules (for courses/diplomados) */}
            {modules.length > 0 && (
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-bold text-gray-900">
                    Contenido del {content?.content_type_info?.name || 'Programa'}
                  </h2>
                  {isRegistered && (
                    <span className="text-sm text-gray-500">
                      {userStatus?.completed_modules?.length || 0} de {modules.length} completados
                    </span>
                  )}
                </div>
                <div className="space-y-3">
                  {modules.map((module, i) => {
                    const isModuleCompleted = userStatus?.completed_modules?.includes(module.id);
                    return (
                      <div
                        key={module.id}
                        onClick={() => canWatch && handleModuleClick(module)}
                        className={`flex items-center gap-4 p-4 rounded-lg border transition-all ${
                          isModuleCompleted
                            ? 'border-green-200 bg-green-50'
                            : module.is_free_preview || canWatch
                              ? 'border-gray-200 hover:bg-gray-50 cursor-pointer'
                              : 'border-gray-100 bg-gray-50'
                        }`}
                      >
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                          isModuleCompleted
                            ? 'bg-green-500 text-white'
                            : 'bg-indigo-100 text-indigo-600'
                        }`}>
                          {isModuleCompleted ? (
                            <CheckCircle className="w-5 h-5" />
                          ) : (
                            i + 1
                          )}
                        </div>
                        <div className="flex-1">
                          <p className={`font-medium ${isModuleCompleted ? 'text-green-700' : 'text-gray-900'}`}>
                            {module.title}
                          </p>
                          {module.duration_minutes && (
                            <p className="text-sm text-gray-500">{module.duration_minutes} min</p>
                          )}
                        </div>
                        {isModuleCompleted ? (
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
                            Completado
                          </span>
                        ) : module.is_free_preview ? (
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
                            Vista previa
                          </span>
                        ) : !canWatch ? (
                          <Lock className="w-4 h-4 text-gray-400" />
                        ) : (
                          <Play className="w-4 h-4 text-indigo-600" />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Materials */}
            {materials.length > 0 && (
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Materiales</h2>
                <div className="space-y-3">
                  {materials.map(material => (
                    <div
                      key={material.id}
                      className="flex items-center gap-4 p-4 rounded-lg border border-gray-200"
                    >
                      <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                        <FileText className="w-5 h-5 text-red-600" />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">{material.title}</p>
                        <p className="text-sm text-gray-500">{material.file_name}</p>
                      </div>
                      {material.can_download ? (
                        <button className="px-3 py-1.5 bg-indigo-100 text-indigo-700 rounded-lg text-sm font-medium hover:bg-indigo-200">
                          <Download className="w-4 h-4" />
                        </button>
                      ) : (
                        <Lock className="w-4 h-4 text-gray-400" />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right Column - CTA Card */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl p-6 shadow-lg sticky top-24">
              {/* Price */}
              <div className="text-center mb-6">
                {content?.is_free ? (
                  <div>
                    <span className="text-4xl font-bold text-green-600">GRATIS</span>
                  </div>
                ) : (
                  <div>
                    {content?.original_price && content.original_price > content.price && (
                      <p className="text-lg text-gray-400 line-through">
                        {formatPrice(content.original_price)}
                      </p>
                    )}
                    <span className="text-4xl font-bold text-gray-900">
                      {formatPrice(content?.price || 0)}
                    </span>
                    <span className="text-gray-500 ml-1">{content?.currency}</span>
                  </div>
                )}
              </div>

              {/* Progress Section (only for registered users) */}
              {isRegistered && (
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">Tu progreso</span>
                    <span className="text-sm font-bold text-indigo-600">
                      {Math.round(userStatus?.progress_percent || 0)}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2.5">
                    <div
                      className="bg-indigo-600 h-2.5 rounded-full transition-all duration-500"
                      style={{ width: `${userStatus?.progress_percent || 0}%` }}
                    />
                  </div>
                  {userStatus?.is_completed && (
                    <div className="flex items-center gap-1 mt-2 text-green-600 text-sm">
                      <CheckCheck className="w-4 h-4" />
                      <span>Contenido completado</span>
                    </div>
                  )}
                </div>
              )}

              {/* CTA Button */}
              {paymentSuccess && (
                <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-4">
                  <div className="flex items-center gap-2 text-green-700">
                    <CheckCircle className="w-5 h-5" />
                    <span className="font-medium">¡Pago exitoso! Ya tienes acceso.</span>
                  </div>
                </div>
              )}
              
              {isRegistered ? (
                <div className="space-y-3">
                  <div className="flex items-center justify-center gap-2 text-green-600 mb-2">
                    <CheckCircle className="w-5 h-5" />
                    <span className="font-medium">Ya estás inscrito</span>
                  </div>
                  
                  {/* Mark as Complete button (if not completed yet) */}
                  {canWatch && !userStatus?.is_completed && (
                    <button
                      onClick={handleMarkComplete}
                      disabled={markingComplete}
                      className="w-full py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:bg-gray-300 flex items-center justify-center gap-2"
                      data-testid="mark-complete-btn"
                    >
                      {markingComplete ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <>
                          <CheckCheck className="w-5 h-5" />
                          Marcar como completado
                        </>
                      )}
                    </button>
                  )}
                  
                  {/* Certificate Section */}
                  {content?.has_certificate && userStatus?.is_completed && (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                      <div className="flex items-center gap-2 text-amber-700 mb-3">
                        <Trophy className="w-5 h-5" />
                        <span className="font-medium">¡Felicidades!</span>
                      </div>
                      
                      {userStatus?.certificate_issued ? (
                        <button
                          onClick={handleDownloadCertificate}
                          className="w-full py-3 bg-amber-600 text-white rounded-lg font-medium hover:bg-amber-700 flex items-center justify-center gap-2"
                          data-testid="download-certificate-btn"
                        >
                          <Download className="w-5 h-5" />
                          Descargar Constancia
                        </button>
                      ) : (
                        <button
                          onClick={handleGenerateCertificate}
                          disabled={generatingCert}
                          className="w-full py-3 bg-amber-600 text-white rounded-lg font-medium hover:bg-amber-700 disabled:bg-gray-300 flex items-center justify-center gap-2"
                          data-testid="generate-certificate-btn"
                        >
                          {generatingCert ? (
                            <>
                              <Loader2 className="w-5 h-5 animate-spin" />
                              Generando...
                            </>
                          ) : (
                            <>
                              <Trophy className="w-5 h-5" />
                              Obtener Constancia
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ) : content?.is_free ? (
                <button
                  onClick={handleRegister}
                  disabled={registering}
                  className="w-full py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:bg-gray-300"
                  data-testid="register-free-btn"
                >
                  {registering ? 'Inscribiendo...' : 'Inscribirme gratis'}
                </button>
              ) : canWatch ? (
                <button
                  onClick={handleRegister}
                  disabled={registering}
                  className="w-full py-3 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 disabled:bg-gray-300"
                  data-testid="register-btn"
                >
                  {registering ? (
                    <Loader2 className="w-5 h-5 animate-spin mx-auto" />
                  ) : (
                    'Inscribirme'
                  )}
                </button>
              ) : (
                <button
                  onClick={handleRegister}
                  disabled={registering || processingPayment}
                  className="w-full py-3 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 disabled:bg-gray-300 flex items-center justify-center gap-2"
                  data-testid="buy-btn"
                >
                  {processingPayment ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Procesando...
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-5 h-5" />
                      Comprar por {formatPrice(content?.price || 0)}
                    </>
                  )}
                </button>
              )}

              {error && (
                <p className="text-red-500 text-sm text-center mt-3">{error}</p>
              )}

              {/* Features */}
              <div className="mt-6 pt-6 border-t border-gray-100 space-y-3">
                {content?.has_certificate && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span className="text-sm">Constancia incluida</span>
                  </div>
                )}
                {content?.has_chat && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span className="text-sm">Chat en vivo</span>
                  </div>
                )}
                {content?.has_qa && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span className="text-sm">Sesión de preguntas</span>
                  </div>
                )}
                <div className="flex items-center gap-2 text-gray-600">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span className="text-sm">Grabación disponible</span>
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span className="text-sm">Acceso de por vida</span>
                </div>
              </div>

              {/* Share */}
              <div className="mt-6 pt-6 border-t border-gray-100 flex justify-center gap-4">
                <button className="p-2 text-gray-400 hover:text-gray-600">
                  <Share2 className="w-5 h-5" />
                </button>
                <button className="p-2 text-gray-400 hover:text-red-500">
                  <Heart className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
